import { useT } from "../../i18n/LanguageProvider";
import type { TranslateFn } from "../../i18n/t";
import {
  readPaymentChoice,
  type PaymentChoice,
  type PaymentProblem,
} from "../../utils/paymentSplit";
import { eur } from "./shopFloorFormat";

/**
 * G-141 (OD-215): "Paid by" Cash / Card on the kiosk — one control for the
 * till, the bouquet builder's Sell and "Customer collected", so the three can
 * never drift. Cash and Card toggle on their own; with both, the florist
 * types the cash part and the card part is the rest of `totalCents`. What a
 * split still lacks is said right under the two boxes — on the tablet the
 * screen's own validation line sits below the fold.
 */
export function ShopFloorPaymentSplit({
  value,
  onChange,
  totalCents,
  idPrefix,
  disabled,
}: {
  value: PaymentChoice;
  onChange: (next: PaymentChoice) => void;
  totalCents: number;
  /** Unique per screen, for the cash box's label. */
  idPrefix: string;
  disabled?: boolean;
}) {
  const { t } = useT();
  const both = value.cash && value.card;
  const reading = readPaymentChoice(value, totalCents);
  const cashId = `${idPrefix}-cash-part`;

  return (
    <div className="shop-floor-sale-payment">
      <span>{t("sale.paidBy")}</span>
      <div role="group" aria-label={t("sale.paidBy")}>
        <button
          type="button"
          aria-pressed={value.cash}
          className={value.cash ? "is-active" : ""}
          onClick={() => onChange({ ...value, cash: !value.cash })}
          disabled={disabled}
        >
          {t("sale.cash")}
        </button>
        <button
          type="button"
          aria-pressed={value.card}
          className={value.card ? "is-active" : ""}
          onClick={() => onChange({ ...value, card: !value.card })}
          disabled={disabled}
        >
          {t("sale.card")}
        </button>
      </div>
      {both ? (
        <div className="payment-split">
          <label htmlFor={cashId}>
            <span>{t("sale.cash")}</span>
            <span className="payment-split-cash">
              €
              <input
                id={cashId}
                type="number"
                inputMode="decimal"
                min={0}
                step={0.01}
                value={value.cashStr}
                onChange={(event) => onChange({ ...value, cashStr: event.target.value })}
                aria-invalid={!reading.ok && reading.problem === "cashNotBelowTotal"}
                disabled={disabled}
              />
            </span>
          </label>
          <div>
            <span>{t("sale.splitCardRest")}</span>
            <strong className="payment-split-card">
              {reading.ok && reading.cardCents != null ? eur(reading.cardCents / 100) : "—"}
            </strong>
          </div>
        </div>
      ) : null}
      {both && !reading.ok ? (
        <p
          className={
            reading.problem === "cashNotBelowTotal"
              ? "payment-split-problem is-error"
              : "payment-split-problem"
          }
          role={reading.problem === "cashNotBelowTotal" ? "alert" : undefined}
        >
          {paymentProblemText(reading.problem, totalCents, t)}
        </p>
      ) : null}
    </div>
  );
}

/** The sentence for what the payment still lacks. */
export function paymentProblemText(
  problem: PaymentProblem,
  totalCents: number,
  t: TranslateFn,
): string {
  if (problem === "pickMethod") return t("sale.pickCashOrCard");
  if (problem === "nothingToSplit") return t("sale.splitNothingToSplit");
  if (problem === "typeCash") return t("sale.splitTypeCash");
  return t("sale.splitCashTooHigh", { total: eur(totalCents / 100) });
}
